import { useEffect, useRef, useState } from 'react';

import { useI18n } from '../i18n/I18nProvider';
import { Button } from './Button';
import './page-frame.css';

/** 多久还没 load 就认为它被对方挡住了。 */
const STUCK_MS = 6000;

/**
 * 在**本页**里开一层外部内容。
 *
 * 这是这个大厅里打开外部页面的**唯一**方式 —— 支付页、独立游戏界面，
 * 以后所有"要跳出去"的东西都走它。
 *
 * ## 为什么不是新窗口
 *
 * `window.open` 在两处必死：
 *
 *   * **手机浏览器的弹窗拦截**。参考工程里那段 `window.open` 已经被注释掉了，
 *     旁边留着一句 "Desligue o bloqueador de pop-ups!" —— 那就是它的下场：
 *     一个需要用户自己去关拦截器的支付流程。
 *   * **H5 压壳成 App 之后**。壳里没有"标签页"这个东西，
 *     新窗口要么被吞掉、要么变成一个退不回来的 WebView。
 *
 * 整页跳走（`location.href`）也不行：那会把大厅整个卸载掉，
 * 回来要重新加载、重新校验会话，而支付这种流程用户是要来回切的。
 *
 * ## 被对方挡住怎么办
 *
 * 很多支付页带 `X-Frame-Options: DENY`，嵌进来就是一片空白。
 * 浏览器**不会**给我们一个可靠的"被挡住了"事件 —— 所以这里用超时兜：
 * 一段时间还没 load 就把提示打出来，让调用方给出的**退路**顶上
 * （支付那边的退路是 PIX 复制粘贴码，它本来就是主路径）。
 */
export function PageFrame(
  { open, url, title, onClose, fallback, bare = false, overlay }:
  {
    open: boolean;
    url: string;
    title: string;
    onClose(): void;
    /** 嵌不进来时显示什么。**必须给** —— 没有退路的话用户就卡死在白屏上。 */
    fallback: React.ReactNode;
    /**
     * 不要顶上那条返回栏，内容铺满（游戏用，见 game/GameBox.tsx）。
     * **这时退路得由 `overlay` 给** —— 没栏又没悬浮键，就只剩安卓返回键了。
     */
    bare?: boolean;
    /** 叠在内容上面的一层（外层壳的 HUD）。卡住兜底那一屏也盖不住它。 */
    overlay?: React.ReactNode;
  },
) {
  const { t } = useI18n();
  const [loaded, setLoaded] = useState(false);
  const [stuck, setStuck] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) { setLoaded(false); setStuck(false); return; }
    timer.current = setTimeout(() => setStuck(true), STUCK_MS);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [open, url]);

  // 开着的时候锁住底下的滚动 —— 不锁的话手指划到边缘会滚到大厅上去
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // 安卓返回键 / 浏览器后退：**关掉这一层，不是退出大厅**
  useEffect(() => {
    if (!open) return;
    const onPop = (e: PopStateEvent) => { e.preventDefault?.(); onClose(); };
    history.pushState({ frame: 1 }, '');
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={`frame${bare ? ' frame--bare' : ''}`}
         role="dialog" aria-modal="true" aria-label={title}>
      {!bare && (
        <header className="frame__bar">
          <Button variant="icon" onClick={onClose} aria-label={t('action.back')}>←</Button>
          <span className="frame__title">{title}</span>
        </header>
      )}

      <div className="frame__body">
        <iframe
          className="frame__inner"
          src={url}
          title={title}
          onLoad={() => { setLoaded(true); setStuck(false); }}
          // 支付页要能提交表单、能跑脚本，就这些。**没给的那几个才是重点**：
          //
          //   没有 allow-popups            → 被嵌的页面**开不了新窗口**。
          //                                  这一条让"不许开新窗口"对第三方内容
          //                                  也成立 —— 由浏览器执行，不靠自觉。
          //   没有 allow-top-navigation    → 它顶不掉整个大厅，绕不过这一层。
          sandbox="allow-forms allow-scripts allow-same-origin"
          referrerPolicy="no-referrer"
        />
        {!loaded && !stuck && <p className="frame__note">{t('action.working')}</p>}

        {stuck && !loaded && (
          // **盖住那片白**：不盖的话用户看到的是一块空白加一行小字
          <div className="frame__fallback">
            <p className="frame__why">{t('frame.blocked')}</p>
            {fallback}
          </div>
        )}
      </div>

      {overlay}
    </div>
  );
}
